<script setup>
  import { onMounted, computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Eraser, Trash } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
  import { LightListContainer } from "@/components/episto-ui/list";
  import { LightListItem } from "@/components/episto-ui/list";
  import { useMAudienceStore } from "@stores/mAudience/mAudience.js";
  import { useLocalesStore } from "@stores/mLocales.js";

  const mLocalesStore = useLocalesStore();
  const { fetchLocales } = mLocalesStore;
  const { locales } = storeToRefs(mLocalesStore);

  const audienceStore = useMAudienceStore();
  const { removeAllLocales, removeLocale, addLocale, isLocaleSelected } = audienceStore;
  const { getMAudienceLocales } = storeToRefs(audienceStore);

  const localesOptionsLoading = ref(false);
  const isLocalesOpen = ref(false);

  const localeSelectOptions = computed(() =>
    locales.value.map((locale) => ({
      value: locale.key,
      label: locale.name,
      disabled: isLocaleSelected(locale.key),
    })),
  );

  const localesCountLabel = computed(() => {
    const count = getMAudienceLocales.value?.length || 0;
    return count > 1 ? `${count} languages` : `${count} language`;
  });

  const handleSelect = (value) => {
    isLocalesOpen.value = true;
    addLocale(value);
  };

  onMounted(async () => {
    localesOptionsLoading.value = true;
    await fetchLocales();
    localesOptionsLoading.value = false;
    if (locales.value.length > 0) isLocalesOpen.value = true;
  });
</script>

<template>
  <h4 class="heading-section mt-4 mb-3">Language</h4>
  <div class="flex flex-col gap-2">
    <span class="text-sm pr-2.5">
      Only enter a language if you need to limit your audience to people who use a language that isn't common to your
      selected locations.
    </span>
    <Select
      class="audience-locales__search__locales"
      filterable
      placeholder="Search languages..."
      :options="localeSelectOptions"
      :loading="localesOptionsLoading"
      @update:model-value="handleSelect" />
  </div>
  <Collapsible
    v-if="getMAudienceLocales?.length"
    :open="isLocalesOpen"
    @update:open="isLocalesOpen = $event">
    <CollapsibleTrigger class="flex w-full items-center justify-between py-2 text-sm font-medium">
      Selected languages
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div class="flex items-center justify-between mb-2">
        <Badge variant="secondary">
          {{ localesCountLabel }} selected
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          @click="removeAllLocales">
          <Eraser class="h-4 w-4 mr-1" />
          <span>Remove all languages</span>
        </Button>
      </div>
      <LightListContainer>
        <LightListItem
          v-for="locale in getMAudienceLocales"
          :key="locale.key"
          :title="locale.name">
          <template #actions>
            <Button
              variant="ghost"
              size="icon-sm"
              @click="removeLocale(locale)">
              <Trash class="h-4 w-4" />
            </Button>
          </template>
        </LightListItem>
      </LightListContainer>
    </CollapsibleContent>
  </Collapsible>
</template>
